/**
 * Copyright Warner Bros. Entertainment, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Warner Bros. Entertainment, Inc. and its suppliers, if any.
 * The intellectual and technical concepts contained herein are
 * proprietary to Warner Bros. Entertainment, Inc. and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in process,
 * and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * unlawful and strictly forbidden unless prior written permission is
 * obtained from Warner Bros. Entertainment, Inc.
 */

import ClassNames from 'classnames';
import {Container} from 'flux/utils';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import {Button, Dropdown, Glyphicon, Modal} from 'react-bootstrap';
import RootCloseWrapper from 'react-overlays/RootCloseWrapper';
import {Link} from 'react-router-dom';
import Scroll from 'react-scroll';

import {MenuIcon, ProfileIcon, CartItemsLoader} from './assets.js';
import HeaderStore from './header-store';
import MobileDrawerMenu from './mobile-drawer';
import {resolveViewportMode} from './viewport.js';
import Config from '../../config/config';
import {HomeActions} from '../../home/home-actions';
import LanguageOptions from '../../messages/language-options';
import {MessagesContext} from '../../messages/messages-context';
import {OrderActions} from '../../orders/order-actions.js';
import OrderStore from '../../orders/order-store';
import {SessionActions, SessionConstants} from '../../session/session-actions';
import SessionStore from '../../session/session-store';
import SerieNavigationStore from '../../titles/serie-navigation/serie-navigation-store';
import TitleStore from '../../titles/title-store';
import {IsNotTriggerKey} from '../../utils/utils';
import CustomDropdown from '../dropdown/';
import HappyFoxModal from '../happy-fox-modal';
import {PageLayoutActions} from '../page-layout/page-layout-actions';
import PageLayoutStore from '../page-layout/page-layout-store';

import './header.less';

// Maximum nav links shown inline before overflow panel activates
const MAX_VISIBLE_NAV_LINKS = 3;
// On desktop, overflow panel activates only when link count exceeds this
const DESKTOP_OVERFLOW_THRESHOLD = 5;

const noop = () => void 0;

/**
 * Navigation items
 * @return {JSX Component} Nav items
 */
class HeaderNavItems extends React.Component {
    static get propTypes() {
        return {
            isMenuOpen: PropTypes.bool,
            isSubHeaderOpen: PropTypes.bool,
            location: PropTypes.object.isRequired,
            onMenuOpen: PropTypes.func,
            onToggleStreaming: PropTypes.func,
            preauthenticated: PropTypes.bool,
            setStreamingLinks: PropTypes.func,
            onLayoutModeChange: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            isMenuOpen: false,
            isSubHeaderOpen: false,
            preauthenticated: false,
            onMenuOpen: noop,
            onToggleStreaming: noop,
            setStreamingLinks: noop,
            onLayoutModeChange: noop
        };
    }

    static calculateState() {
        return {
            itemsInCart: OrderStore.getState().get('itemsInCart'),
            navigationItems: HeaderStore.getState().get('navigationItems'),
            parentUser: SessionStore.getState().get('parentUser'),
            searchBar: PageLayoutStore.getState().get('searchBar'),
            serieNavigation: SerieNavigationStore.getState().get('serieNavigation'),
            title: TitleStore.getState().get('title'),
            user: SessionStore.getState().get('authUser')
        };
    }

    static getStores() {
        return [SessionStore, PageLayoutStore, OrderStore, SerieNavigationStore, TitleStore, HeaderStore];
    }

    constructor(props) {
        super(props);

        this.state = Object.assign({
            isLanguageModalOpen: false,
            isMenuOpen: false,
            showAssetRequestsModal: false,
            width: window.innerWidth,
            isMobileDrawerOpen: false,
            layoutMode: resolveViewportMode(window.innerWidth)
        }, this.constructor.calculateState());

        this.closeMenu = this.closeMenu.bind(this);
        this.handleLogoutKeyUp = this.handleLogoutKeyUp.bind(this);
        this.handleNavMenuClicks = this.handleNavMenuClicks.bind(this);
        this.handleToggleLanguageModal = this.handleToggleLanguageModal.bind(this);
        this.handleToggleMenu = this.handleToggleMenu.bind(this);
        this.isActive = this.isActive.bind(this);
        this.toggleAssetRequestsModal = this.toggleAssetRequestsModal.bind(this);
        this.toggleSearchBar = this.toggleSearchBar.bind(this);
        this.toggleSearchBarKeyUp = this.toggleSearchBarKeyUp.bind(this);
    }

    componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        this.props.onLayoutModeChange(this.state.layoutMode);
        if (!this.props.preauthenticated && SessionStore.isSessionValid()) {

            if (!this.state.navigationItems.size) {
                // Prevents empty nav if we are not on the home page and the user refreshes
                HomeActions.getHomepage();
            }

            OrderActions.getCartItemsAmount();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            this.state.navigationItems !== prevState.navigationItems ||
            this.state.width !== prevState.width
        ) {
            let navLinks = [];

            if (this.state.navigationItems.size) {
                navLinks = this.state.navigationItems.map((li) => (
                    <Link
                        className={this.isActive(li.get('targetUrl'))}
                        onClick={this.handleNavMenuClicks}
                        to={li.get('targetUrl', '').trim()}
                    >
                        {li.get('displayName')}
                    </Link>
                )).toArray();
            }

            const mode = this.state.layoutMode;
            const hasManyLinks = navLinks.length > DESKTOP_OVERFLOW_THRESHOLD;

            const shouldOverflow =
                (mode === 'desktop' && hasManyLinks) ||
                mode === 'tablet' ||
                mode === 'mobile';

            let overflowLinks = [];

            if (shouldOverflow) {
                overflowLinks = navLinks.slice(MAX_VISIBLE_NAV_LINKS);
            }

            this.props.setStreamingLinks(overflowLinks);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize = () => {
        const width = window.innerWidth;
        const layoutMode = resolveViewportMode(width);
        this.setState({width, layoutMode});
        this.props.onLayoutModeChange(layoutMode);
    };

    closeMobileDrawer = () => {
        this.setState({isMobileDrawerOpen: false});
    };

    static contextType = MessagesContext;

    closeMenu(isUserMenuLink) {
        if (isUserMenuLink === true) {
            this.props.onMenuOpen(false);
        }
        this.setState({
            isMenuOpen: false
        });
    }

    handleNavMenuClicks() {
        if (this.props.isMenuOpen) {
            this.props.onMenuOpen(this.props.isMenuOpen);
        }
    }

    handleToggleLanguageModal() {
        this.setState(prevState => ({
            isLanguageModalOpen: !prevState.isLanguageModalOpen
        }));
        return;
    }

    handleToggleMenu(isOpen) {
        this.setState({
            isMenuOpen: isOpen
        });
    }

    isActive(path) {
        if (this.props.location.pathname === path) {
            return 'active';
        }
    }

    toggleSearchBar() {
        if (this.state.searchBar.get('fixed')) {
            PageLayoutActions.hideSearchBar();
        } else {
            PageLayoutActions.showSearchBar();
        }
    }

    toggleSearchBarKeyUp(ev) {
        if (ev && IsNotTriggerKey(ev)) {
            ev.preventDefault();

            return;
        }

        this.toggleSearchBar();
    }

    handleLogoutKeyUp(ev) {
        if (ev && IsNotTriggerKey(ev)) {
            ev.preventDefault();

            return;
        }

        SessionActions.logout();
    }

    toggleAssetRequestsModal(show) {
        this.handleNavMenuClicks();
        this.setState(() => ({
            showAssetRequestsModal: show
        }));
    }

    toggleAssetRequestsModalKeyUp(show, ev) {
        if (ev && IsNotTriggerKey(ev)) {
            ev.preventDefault();

            return;
        }

        this.toggleAssetRequestsModal(show);
    }

    hasBrowseOrdersPermission() {
        return SessionStore.canUser(SessionStore.PERMISSIONS.ORDERS.BROWSE);
    }

    hasHappyFoxPermission() {
        return SessionStore.canUser(SessionStore.PERMISSIONS.HAPPYFOX.REQUESTS.SUBMIT);
    }

    hasViewCartPermission() {
        return SessionStore.canUser(SessionStore.PERMISSIONS.VIEW.CART);
    }

    render() {
        if (!SessionStore.isSessionValid() || SessionStore.canUser(SessionStore.PERMISSIONS.RESTRICTED.HOMEPAGEONLY)) {
            return null;
        }

        let itemCount = this.state.itemsInCart;
        if (itemCount === undefined) {
            itemCount = '0';
        }
        let cart;
        let order;
        let separator;


        if (this.hasViewCartPermission()) {
            cart = (
                <li>
                    <Link
                        onClick={this.closeMenu.bind(this, true)}
                        to="/cart"
                    >{this.context.intl.messages['header.navbar.dropdown.item.cart']} ({itemCount})</Link>
                </li>
            );
            separator = <li role="separator" className="divider"/>;
        }

        if (this.hasBrowseOrdersPermission()) {
            order = (
                <li>
                    <Link
                        onClick={this.closeMenu.bind(this, true)}
                        to="/orders"
                    >{this.context.intl.messages['header.navbar.dropdown.item.orders']}</Link>
                </li>
            );
            separator = <li role="separator" className="divider"/>;
        }

        // HappyFox "Asset Requests" modal
        let happyFoxRequest;
        if (this.hasHappyFoxPermission() && !SessionStore.isContentNotAvailable()) {
            happyFoxRequest = (
                <li className="header-nav-link header-nav-requests-link">
                    <a href="#" className="request-button" onClick={this.toggleAssetRequestsModal.bind(this, true)} onKeyUp={this.toggleAssetRequestsModalKeyUp.bind(this, true)}>
                        <span className="header-nav-requests">{this.context.intl.messages['header.navbar.item.requests']}</span>
                    </a>
                </li>
            );
        }

        // Partner Links
        let navLinks = [];
        let visibleLinks = [];
        let overflowLinks = [];
        if (!SessionStore.isMultipleTitles() && !SessionStore.isSingleTitle() && !SessionStore.isContentNotAvailable()) {
            // Include this.state.links in conditional to prevent blank page after login when partner not configured above in PARTNER_LINKS
            if (this.state.navigationItems.size) {
                navLinks = this.state.navigationItems.map((li) => (
                    <Link
                        className={this.isActive(li.get('targetUrl'))}
                        onClick={this.handleNavMenuClicks}
                        to={li.get('targetUrl', '').trim()}
                    >
                        {li.get('displayName')}
                    </Link>
                )).toArray();
            }
            const mode = this.state.layoutMode;

            let maxVisible = navLinks.length;

            if (mode !== 'desktop' || navLinks.length > DESKTOP_OVERFLOW_THRESHOLD) {
                maxVisible = MAX_VISIBLE_NAV_LINKS;
            }

            visibleLinks = navLinks.slice(0, maxVisible);
            overflowLinks = navLinks.slice(maxVisible);
        }

        let userMenuLabel = `User Menu: ${this.state.user.get('fullName', '')} logged in. ${itemCount} items in the cart.`;
        let userFullName = this.state.user.get('fullName', '');
        if (this.state.user.get('accountType') === SessionConstants.USER_TYPES.SECONDARY.id) {
            userFullName = `${this.state.parentUser.get('name')} ${this.state.parentUser.get('lastName')}`;
        }

        let cartItemsLabel = <span className="label user-count">
            <img className="cart-items-loader" alt="loading..." role="presentation" src={CartItemsLoader}/>
        </span>;

        if (this.state.itemsInCart !== undefined) {
            cartItemsLabel = <span className="label bg-wb-blue user-count">{itemCount}</span>;
        }

        let profileIconLabel = '';
        if (this.state.isMenuOpen) {
            profileIconLabel = (
                <div className="user-menu-label">
                    {<span className="user-name">{userFullName}&nbsp;</span>}
                    {cartItemsLabel}
                    <span className="caret"/>
                </div>
            );
        } else {
            profileIconLabel = (
                <div className="profile-icon-wrapper">
                    <img
                        src={ProfileIcon}
                        alt="menu"
                        className="navbar-icon"
                    />
                </div>
            );
        }

        return (
            <div className={ClassNames('header-item header-actions', {'hide-navbar': this.state.layoutMode === 'mobile' && !this.props.isMenuOpen})}>
                <nav id="navbar" className="navbar-collapse">
                    <ul className="nav navbar-nav navbar-right-header header-nav-links">
                        {this.state.layoutMode !== 'mobile' && visibleLinks.map((link, i) => (
                            <li key={i} className="header-nav-link">{link}</li>
                        ))}
                        <RootCloseWrapper
                            onRootClose={() => {
                                if (this.props.isSubHeaderOpen) {
                                    this.props.onToggleStreaming();
                                }
                            }}
                        >
                            {(this.state.layoutMode === 'tablet' || this.state.layoutMode === 'desktop') && overflowLinks.length > 0 && (
                                <li className={ClassNames('header-nav-link', {'active': this.props.isSubHeaderOpen})} >
                                    <button
                                        className="header-item-dropdown-toggle"
                                        onClick={this.props.onToggleStreaming}
                                    >
                                        {this.context.intl.messages['header.navbar.dropdown.item.more']}&nbsp;
                                        <span
                                            className={ClassNames('custom-chevron', {
                                                'custom-chevron-open': this.props.isSubHeaderOpen
                                            })}
                                        />
                                    </button>
                                </li>
                            )}
                        </RootCloseWrapper>
                    </ul>

                    <ul className="nav navbar-nav navbar-right-header">
                        {this.state.layoutMode === 'mobile' &&
                        <Button
                            aria-controls="navbar"
                            aria-expanded="false"
                            className="header-item navbar-toggle-header"
                            data-target="#navbar"
                            id="navbar-header-button"
                            onClick={() => this.setState({isMobileDrawerOpen: true})}
                            type="button"
                        >
                            <span className="sr-only">Toggle navigation</span>
                            <img
                                src={MenuIcon}
                                alt="menu"
                                className="navbar-icon"
                            />
                        </Button>
                        }
                        {happyFoxRequest}

                        <RootCloseWrapper onRootClose={this.closeMenu}>
                            <Dropdown id="nav-menu" className="dropdown-menu-profile" componentClass="li" pullRight onToggle={this.handleToggleMenu} open={this.state.isMenuOpen} aria-label={userMenuLabel}>
                                <CustomDropdown.Toggle bsRole="toggle" onClick={noop}>
                                    {profileIconLabel}
                                </CustomDropdown.Toggle>
                                <CustomDropdown.Menu bsRole="menu">
                                    {cart}
                                    {separator}
                                    {order}
                                    {separator}
                                    {SessionStore.canUser(SessionStore.PERMISSIONS.MY_ACCOUNT.TABS.INFO.SUBSCRIPTIONS) && <li>
                                        <Link
                                            className="nav-menu-link"
                                            onClick={this.closeMenu.bind(this, true)}
                                            to="/account/subscriptions"
                                        >{this.context.intl.messages['header.navbar.dropdown.item.subscriptions']}</Link>
                                    </li>}
                                    {SessionStore.canUser(SessionStore.PERMISSIONS.MY_ACCOUNT.TABS.INFO.SUBSCRIPTIONS) && <li role="separator" className="divider"/>}
                                    {SessionStore.canUser(SessionStore.PERMISSIONS.MY_ACCOUNT.TABS.INFO.ACCOUNT_INFO) && <li>
                                        <Link
                                            className="nav-menu-link"
                                            onClick={this.closeMenu.bind(this, true)}
                                            to="/account"
                                        >{this.context.intl.messages['header.navbar.dropdown.item.account']}</Link>
                                    </li>}
                                    {SessionStore.canUser(SessionStore.PERMISSIONS.MY_ACCOUNT.TABS.INFO.ACCOUNT_INFO) && <li role="separator" className="divider"/>}
                                    <li>
                                        <a href="#" onClick={this.handleToggleLanguageModal}>{this.context.intl.messages['common.change-language']}</a>
                                    </li>
                                    <li role="separator" className="divider"/>
                                    <li>
                                        <a href="#" onClick={SessionActions.logout} onKeyUp={this.handleLogoutKeyUp}>{this.context.intl.messages['header.navbar.dropdown.item.logout']}</a>
                                    </li>
                                </CustomDropdown.Menu>
                            </Dropdown>
                            <Modal show={this.state.isLanguageModalOpen} onHide={this.handleToggleLanguageModal} bsSize="small" className="change-language-modal">
                                <Modal.Header className="bg-gray" closeButton>
                                    <Modal.Title className="modal-title text-center">{this.context.intl.messages['common.change-language']}</Modal.Title>
                                </Modal.Header>
                                <Modal.Body className="text-center">
                                    <LanguageOptions showAs="header" className="btn-sm text-center" variant="secondary btn-sm" />
                                </Modal.Body>
                            </Modal>
                        </RootCloseWrapper>
                    </ul>
                </nav>
                <HappyFoxModal
                    showAssetRequestsModal={this.state.showAssetRequestsModal}
                    onToggleModal={this.toggleAssetRequestsModal}
                />
                <MobileDrawerMenu
                    isOpen={
                        this.state.layoutMode === 'mobile' &&
                        this.state.isMobileDrawerOpen
                    }
                    visibleLinks={visibleLinks}
                    overflowLinks={overflowLinks}
                    closeDrawer={this.closeMobileDrawer}
                    moreLabel={
                        this.context.intl.messages[
                            'header.navbar.dropdown.item.more'
                        ]
                    }
                />
            </div>
        );
    }
}

class PreauthenticatedHeaderNavItems extends React.Component {
    static get propTypes() {
        return {
            userName: PropTypes.string.isRequired
        };
    }

    constructor(props) {
        super(props);
        this.state = {
            isMenuOpen: false,
        };

        this.closeMenu = this.closeMenu.bind(this);
        this.handleToggleMenu = this.handleToggleMenu.bind(this);
    }

    static contextType = MessagesContext;

    closeMenu() {
        this.setState({
            isMenuOpen: false
        });
    }

    handleToggleMenu(isOpen) {
        this.setState({
            isMenuOpen: isOpen
        });
    }

    render() {
        let menuLink = (
            <li>
                <Link to="/login">{this.context.intl.messages['header.navbar.dropdown.item.login']}</Link>
            </li>
        );

        if (SessionStore.isSessionValid()) {
            menuLink = (
                <li>
                    <Link to="/">{this.context.intl.messages['header.navbar.dropdown.item.home']}</Link>
                </li>
            );
        }

        return (
            <div id="navbar" className="navbar-collapse collapse">
                <ul className="nav navbar-nav navbar-right">
                    <RootCloseWrapper onRootClose={this.closeMenu}>
                        <Dropdown id="nav-menu" componentClass="li" pullRight onToggle={this.handleToggleMenu} open={this.state.isMenuOpen}>
                            <CustomDropdown.Toggle bsRole="toggle">
                                {this.props.userName}&nbsp;
                                <span className="caret"/>
                            </CustomDropdown.Toggle>
                            <CustomDropdown.Menu bsRole="menu">
                                {menuLink}
                            </CustomDropdown.Menu>
                        </Dropdown>
                    </RootCloseWrapper>
                </ul>
            </div>
        );
    }
}

/**
 * Header Component
 * @return {JSX Component} Page header with navigation
 */
export default class Header extends React.Component {
    static get propTypes() {
        return {
            location: PropTypes.object.isRequired,
            partnerId: PropTypes.string.isRequired,
            preauthenticated: PropTypes.bool,
            searchIconHidden: PropTypes.bool.isRequired,
            userName: PropTypes.string
        };
    }

    static get defaultProps() {
        return {
            preauthenticated: false,
            userName: undefined
        };
    }

    constructor(props) {
        super(props);

        this.state = {
            isBrandsMenuOpen: false,
            isMenuOpen: false,
            isSubHeaderOpen: false,
            streamingLinks: [],
            layoutMode: 'desktop'
        };

        this.handleCloseBrandsMenu = this.handleCloseBrandsMenu.bind(this);
        this.handleLogoutKeyUp = this.handleLogoutKeyUp.bind(this);
        this.handleSearchClick = this.handleSearchClick.bind(this);
        this.handleToggleBrandsMenu = this.handleToggleBrandsMenu.bind(this);
        this.handleToggleMenu = this.handleToggleMenu.bind(this);
        this.redirectToMultiBrandHub = this.redirectToMultiBrandHub.bind(this);
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevState.layoutMode !== this.state.layoutMode &&
            this.state.isSubHeaderOpen
        ) {
            const header = document.getElementById('layout-header');

            if (!header) {
                return;
            }

            if (this.state.layoutMode === 'mobile') {
                PageLayoutActions.setSearchBarOffset(header.clientHeight);
            } else {
                PageLayoutActions.setSearchBarOffset(header.clientHeight * 2);
            }
        }
    }

    static contextType = MessagesContext;

    toggleStreaming = () => {
        const header = document.getElementById('layout-header');

        this.setState(prev => {
            if (!prev.isSubHeaderOpen) {
                PageLayoutActions.setSearchBarOffset(header.clientHeight * 2);
            } else {
                PageLayoutActions.setSearchBarOffset(header.clientHeight);
            }
            return {
                isSubHeaderOpen: !prev.isSubHeaderOpen
            };
        });
    };

    setStreamingLinks = (links) => {
        this.setState({streamingLinks: links});
    };

    setLayoutMode = (layoutMode) => {
        this.setState({layoutMode});
    };

    handleLogoutKeyUp(ev) {
        if (ev && IsNotTriggerKey(ev)) {
            ev.preventDefault();

            return;
        }

        SessionActions.logout();
    }

    handleCloseBrandsMenu() {
        this.setState(() => ({
            isBrandsMenuOpen: false
        }));
    }

    handleNavigateToPartner(partner) {
        const auth = SessionStore.getAuthorization();
        if (partner.isBrand) {
            const currentBrand = SessionStore.getCurrentBrand();
            if (currentBrand && currentBrand.get('id') === partner.id) {
                SessionActions.deleteUserSessionBrand(auth);
            } else {
                SessionActions.updateUserSessionBrand(partner, auth);
            }
        } else if (this.props.partnerId === partner.id) {
            SessionActions.deleteUserSessionBrand(auth);
        } else {
            SessionActions.navigateToPartner(partner, auth);
        }
    }

    handleNavigateToPartnerKeyUp(partner, ev) {
        if (ev && IsNotTriggerKey(ev)) {
            ev.preventDefault();

            return;
        }

        this.handleNavigateToPartner(partner);
    }

    handleSearchClick() {
        this.handleToggleMenu(false);
        Scroll.animateScroll.scrollToTop();
    }

    handleToggleBrandsMenu(isOpen) {
        this.setState(() => ({
            isBrandsMenuOpen: isOpen
        }));
    }

    handleToggleBrandsMenuKeyUp(isOpen, ev) {
        if (ev && IsNotTriggerKey(ev)) {
            ev.preventDefault();

            return;
        }

        this.handleToggleBrandsMenu(isOpen);
    }

    handleToggleMenu(isOpen) {
        this.setState(() => ({
            isMenuOpen: isOpen
        }));
    }

    redirectToMultiBrandHub() {
        window.location.replace('/ts/brands-hub');
    }

    render() {
        let header;
        const PARTNER = SessionStore.getPartner();
        let partnerLogo = `/${PARTNER.logo}`;
        if (PARTNER.isBrandActive || SessionStore.isBrandsUser()) {
            partnerLogo = `/${PARTNER.brandLogo}`;
        }
        if (SessionStore.isACSUser() && PARTNER.acsLogo) {
            partnerLogo = `/${PARTNER.acsLogo}`;
        }
        // BRAIN-4625: Display airlinesLogo
        if (PARTNER.id === Config.Partners.HBO.id) {
            partnerLogo = `/${PARTNER.airlinesLogo}`;
        }
        if (PARTNER.id === Config.Partners.HBO_MAX_PORTAL.id) {
            partnerLogo = `/${PARTNER.airlinesLogo}`;
        }

        let searchIconHidden = this.props.searchIconHidden;

        switch (true) {
        case Config.Partners.PRESS_SITE.id === this.props.partnerId:
        case SessionStore.isContentNotAvailable():
            searchIconHidden = true;
            break;
        }

        if (!SessionStore.canUser(SessionStore.PERMISSIONS.RESTRICTED.HOMEPAGEONLY)) {
            const userPartners = SessionStore.getUserPartners();
            let filteredPartners = [];
            if (userPartners) {
                filteredPartners = userPartners.map(p => {
                    const clientId = p.get('clientId');
                    return Config.Partners[clientId];
                }).toJS();
            }

            const userBrands = Immutable.List(SessionStore.getUserBrands());
            userBrands.forEach(brand => {
                filteredPartners.push({
                    baseUrl: '', // Note: This will prevent any userBrand from appearing in the waffle menu, the baseUrl is not used for userBrands
                    description: brand.get('description'),
                    id: brand.get('id'),
                    key: '',
                    loginBaseUrl: '',
                    logo: brand.get('logoSourceUrl'),
                    isBrand: true,
                });
            });

            // BRAIN-4101: prevent partners without baseUrl or logo from being displayed
            filteredPartners = filteredPartners.filter(partner => partner?.baseUrl && partner?.logo);

            let brandsMenu;
            if (SessionStore.canUser(SessionStore.PERMISSIONS.HEADER.BRANDS_MENU) && filteredPartners.length > 1) {
                brandsMenu = (
                    <RootCloseWrapper onRootClose={this.handleCloseBrandsMenu}>
                        <Dropdown
                            id="brands-nav-menu"
                            className="brands-nav-menu header-item"
                            componentClass="div"
                            onToggle={this.handleToggleBrandsMenu}
                            open={this.state.isBrandsMenuOpen}
                        >
                            <CustomDropdown.Toggle bsRole="toggle" className="brands-nav-menu-toggle">
                                <span className="brand-nav-icon" aria-label={this.context.intl.messages['header.brands.toggle.nav']}>
                                    <i className="glyphicon glyphicon-th"/>
                                </span>
                            </CustomDropdown.Toggle>
                            <CustomDropdown.Menu bsRole="menu">
                                <h4>{this.context.intl.messages['header.brands.dropdown.select']}&nbsp;
                                    <small className="pull-right">
                                        <a
                                            role="button"
                                            tabIndex="0"
                                            onClick={this.handleToggleBrandsMenu.bind(this, false)}
                                            onKeyUp={this.handleToggleBrandsMenuKeyUp.bind(this, false)}
                                        >
                                            {this.context.intl.messages['common.close'].toUpperCase()}
                                        </a>
                                    </small>
                                </h4>
                                {Object.keys(filteredPartners).filter(k => filteredPartners[k] && !!filteredPartners[k].logo).map((k, i) => {
                                    const logo = filteredPartners[k].logo;
                                    let logoUrl = `url(/${logo})`;
                                    let active = false;
                                    if (filteredPartners[k].isBrand) {
                                        if (PARTNER.isBrandActive) {
                                            active = PARTNER.brandId === filteredPartners[k].id;
                                        }
                                    } else {
                                        if (!PARTNER.isBrandActive) {
                                            active = PARTNER.id === filteredPartners[k].id;
                                        }
                                    }

                                    return (
                                        <li className="brand-logo" key={`partner-${i}`}>
                                            <span
                                                role="button"
                                                className={ClassNames('brand-image-container', {active})}
                                                onClick={this.handleNavigateToPartner.bind(this, filteredPartners[k])}
                                                onKeyUp={this.handleNavigateToPartnerKeyUp.bind(this, filteredPartners[k])}
                                                tabIndex="0"
                                            >
                                                <div style={{backgroundImage: logoUrl}}/>
                                            </span>
                                        </li>
                                    );
                                })}
                                <li className="brand-logo" style={{verticalAlign: 'Top', color: 'White', fontSize: '20px'}}>
                                    <div
                                        role="button"
                                        className={'brand-image-container'}
                                        style={{display: 'flex', alignItems: 'center'}}
                                        onClick={this.redirectToMultiBrandHub}
                                        onKeyUp={this.redirectToMultiBrandHub}
                                        tabIndex="0"
                                    >
                                        <i className="fas fa-plus fa-1x"></i>&nbsp;{this.context.intl.messages['home.section.list.view-all']}
                                    </div>
                                </li>
                            </CustomDropdown.Menu>
                        </Dropdown>
                    </RootCloseWrapper>
                );
            }
            let headerItems = (
                <HeaderNavItemsContainer
                    isMenuOpen={this.state.isMenuOpen}
                    location={this.props.location}
                    onMenuOpen={this.handleToggleMenu.bind(this, false)}
                    partnerId={this.props.partnerId}
                    searchIconHidden={searchIconHidden}
                    onToggleStreaming={this.toggleStreaming}
                    setStreamingLinks={this.setStreamingLinks}
                    isSubHeaderOpen={this.state.isSubHeaderOpen}
                    onLayoutModeChange={this.setLayoutMode}
                />
            );

            if (this.props.preauthenticated) {
                headerItems = <PreauthenticatedHeaderNavItems location={this.props.location} userName={this.props.userName} />;
            }

            header = (
                <>
                    {brandsMenu}
                    <h1>
                        <Link className="header-item brand-logo" to="/" onClick={() => PageLayoutActions.changeSearchBarDropdown('all')}>
                            <img
                                alt={`${this.context.intl.messages['login.brand.name']} - ${this.context.intl.messages['header.logo']}`}
                                src={partnerLogo}
                            />
                        </Link>
                    </h1>
                    {headerItems}
                </>
            );
        } else {
            let user = SessionStore.getState().getIn(['authUser', 'name'], 'User');
            header = (
                <div className="pull-right-sm-up text-center padding-x-10">
                    Welcome, {user}!
                    <a role="button" tabIndex="0" onClick={SessionActions.logout} onKeyUp={this.handleLogoutKeyUp} className="btn btn-sm btn-secondary margin-left-10">Log Out <Glyphicon glyph="log-out"/></a>
                </div>
            );
        }
        let partnerClassName;
        if (this.props.partnerId) {
            partnerClassName = `${this.props.partnerId.toLowerCase()}-header`;
        }
        return (
            <header id="layout-header" className={ClassNames('header-scroll', partnerClassName)}>
                <div className="header-main">
                    {header}
                </div>
                {this.state.isSubHeaderOpen && this.state.layoutMode !== 'mobile' && <nav className="sub-nav">
                    <ul className="nav navbar-nav">
                        {this.state.streamingLinks.map((link, i) => (
                            <li key={i} className="header-nav-link">{link}</li>
                        ))}
                    </ul>
                </nav>}
            </header>
        );
    }
}

const HeaderNavItemsContainer = Container.create(HeaderNavItems);
export {
    HeaderNavItemsContainer as HeaderNavItems,
    HeaderNavItems as HeaderNavItemsRaw
};
